import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowsRotateRight, Xmark } from '@gravity-ui/icons';
import ViolationStateBlock from './ViolationStateBlock';
import ActivationEditor from './ActivationEditor';
import ActivationSummary from './ActivationSummary';
import RuleStatusBadge from './RuleStatusBadge';
import RuleOriginBadge from './RuleOriginBadge';
import RetentionFields from './RetentionFields';
import {
  getViolationRuleV2,
  deactivateViolationRule,
  updateViolationRuleActivation,
  updateViolationRuleRetention,
} from '../../../services/violations';
import {
  EMPTY_RETENTION,
  fromRetentionDto,
  toRetentionDto,
  validateRetention,
} from '../../../utils/violationRetention';
import { formatRuleCondition, featuresLabel, metricLabel } from '../../../utils/ruleFormat';
import { useRuleCatalog } from '../../../hooks/useRuleCatalog';
import { describeUserError, extractErrorMessage } from '../../../utils/apiError';
import { describeConflict } from '../../../utils/conflictMessages';
import {
  fromActivationDto,
  toActivationDto,
  validateActivation,
} from '../../../utils/violationSchedule';
import {
  RULE_STATE,
  deriveRuleState,
} from '../../../constants/violationConstants';
import { getZoneScopeLabel } from '../../../utils/ruleZones';
import { describeRecurrence } from '../../../utils/ruleRecurrence';
import { formatCooldownShort } from '../../../utils/ruleCooldown';
import { formatBoostShort } from '../../../utils/ruleSamplingBoost';
import { getSamplingBoost, getSamplingEffective } from '../../../services/samplingBoost';
import { getCamera } from '../../../services/cameras';
import { isAdmin } from '../../../services/api';
import { formatInTimezone } from '../../../utils/formatInTimezone';

/*
 * P2-01 / Rule Schedule.
 * Блок активации presence-driven: он появляется, только когда backend начинает
 * отдавать rule.activation. Правка расписания идёт через PUT .../activation с
 * expected_schedule_version, поэтому чужие изменения не затираются.
 */

// Пауза показывается, только пока она ещё не закончилась.
const isCooldownActive = (until) => {
  if (!until) return false;
  const time = new Date(until).getTime();
  return !Number.isNaN(time) && time > Date.now();
};

const ViolationRuleViewDrawer = ({
  ruleId,
  ruleOverride = null,
  cameraLabel,
  canEdit = false,
  busy = false,
  onActivate,
  onDeactivate,
  onDelete,
  onEdit,
  onRefetch,
  onActivationSaved,
  onClose,
}) => {
  const [rule, setRule] = useState(null);
  const [state, setState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const { catalog } = useRuleCatalog(rule?.camera_id);
  const catalogZones = useMemo(() => catalog?.zones || [], [catalog]);

  // Ускорение детекций и текущий интервал камеры — только для админа.
  // Ответ учитывается, пока открыто то же правило (отмена в cleanup).
  const [boostInfo, setBoostInfo] = useState({ status: 'idle', config: null, effective: null });
  const [cooldownTimezone, setCooldownTimezone] = useState(undefined);
  const [cooldownTzReady, setCooldownTzReady] = useState(false);
  const viewRuleId = rule?.rule_id;
  const viewCameraId = rule?.camera_id;
  const hasCooldownUntil = Boolean(rule?.cooldown_until);

  useEffect(() => {
    setBoostInfo({ status: 'idle', config: null, effective: null });
    if (!viewRuleId || !viewCameraId || !isAdmin()) return undefined;
    let cancelled = false;
    setBoostInfo({ status: 'loading', config: null, effective: null });
    // Сбой текущего интервала камеры не прячет настройку самого правила.
    Promise.allSettled([getSamplingBoost(viewRuleId), getSamplingEffective(viewCameraId)])
      .then(([boostResult, effectiveResult]) => {
        if (cancelled) return;
        if (boostResult.status !== 'fulfilled') {
          setBoostInfo({ status: 'error', config: null, effective: null });
          return;
        }
        setBoostInfo({
          status: 'ready',
          config: boostResult.value,
          effective: effectiveResult.status === 'fulfilled' ? effectiveResult.value : null,
        });
      });
    return () => { cancelled = true; };
  }, [viewRuleId, viewCameraId]);

  useEffect(() => {
    setCooldownTimezone(undefined);
    // Без камеры часовой пояс не узнать — сразу показываем время в поясе по умолчанию.
    setCooldownTzReady(!viewCameraId);
    if (!hasCooldownUntil || !viewCameraId) return undefined;
    let cancelled = false;
    getCamera(viewCameraId)
      .then((camera) => { if (!cancelled) setCooldownTimezone(camera?.display_timezone || undefined); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCooldownTzReady(true); });
    return () => { cancelled = true; };
  }, [hasCooldownUntil, viewCameraId]);

  const [editing, setEditing] = useState(false);
  const [activationModel, setActivationModel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Текст 409 — из describeConflict, а не хардкод: код конфликта решает,
  // какую фразу показать (ТЗ 6, 19).
  const [conflict, setConflict] = useState('');

  const [retentionEditing, setRetentionEditing] = useState(false);
  const [retentionModel, setRetentionModel] = useState(EMPTY_RETENTION);
  const [retentionErrors, setRetentionErrors] = useState({});
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionError, setRetentionError] = useState('');
  const [retentionConflict, setRetentionConflict] = useState('');

  // Гейт «деактивировать → перечитать → редактировать» (ТЗ 1, 2): null,
  // пока карточка просто просматривается, иначе описывает шаг диалога.
  const [editGate, setEditGate] = useState(null);

  // Вынесено отдельно от loadRule, чтобы gate мог перечитать правило и
  // получить свежие данные без побочных эффектов сброса editing/retentionEditing.
  const fetchRule = useCallback(() => getViolationRuleV2(ruleId), [ruleId]);

  const loadRule = useCallback(() => {
    if (!ruleId) return undefined;
    let cancelled = false;
    setState('loading');

    fetchRule()
      .then((data) => {
        if (cancelled) return;
        setRule(data);
        setState('ready');
        setConflict('');
        setEditing(false);
        setRetentionEditing(false);
        setEditGate(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(describeUserError(error, 'Не удалось загрузить правило'));
        setState('error');
      });

    return () => { cancelled = true; };
  }, [ruleId, fetchRule]);

  useEffect(() => loadRule(), [loadRule]);

  /**
   * PART 3, раздел 2: родитель может обновить данные правила через lifecycle-действие
   * (activate/deactivate/delete). Когда ruleOverride меняется (т.е. родитель обновил свой
   * список), мёржим актуальные поля в загруженную карточку, не затирая поля, которых
   * нет в списке (например activation, загруженное отдельным запросом).
   *
   * Зависимости — только [ruleOverride], чтобы не вызывать эффект на каждом изменении
   * rule (которое будет на каждом спреде). Если ruleOverride не установлен, эффект не
   * запускается.
   */
  useEffect(() => {
    if (!ruleOverride) return;
    setRule((prev) => (prev ? { ...prev, ...ruleOverride } : prev));
  }, [ruleOverride]);

  const startRetentionEditing = () => {
    setRetentionModel(fromRetentionDto(rule));
    setRetentionErrors({});
    setRetentionError('');
    setRetentionConflict('');
    setRetentionEditing(true);
  };

  /**
   * Part 4, раздел 3.4: сроки хранения меняются отдельным PUT .../retention.
   * Источник истины — ответ backend, локально пару полей не досочиняем.
   */
  const saveRetention = () => {
    const validation = validateRetention(retentionModel);
    setRetentionErrors(validation.errors);
    if (!validation.valid) return;

    setRetentionSaving(true);
    setRetentionError('');
    setRetentionConflict('');

    updateViolationRuleRetention(ruleId, toRetentionDto(retentionModel))
      .then((updated) => {
        setRule((prev) => (updated ? { ...prev, ...updated } : prev));
        setRetentionEditing(false);
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          // Форму не закрываем и не перетираем — пользователь сам решает,
          // перечитать чужую версию или повторить своё изменение.
          setRetentionConflict(describeConflict(error, 'rule_retention').message);
          return;
        }
        setRetentionError(extractErrorMessage(error, 'Не удалось сохранить сроки хранения'));
      })
      .finally(() => setRetentionSaving(false));
  };

  const startEditing = () => {
    setActivationModel(fromActivationDto(rule.activation));
    setSaveError('');
    setConflict('');
    setEditing(true);
  };

  const saveActivation = () => {
    if (!validateActivation(activationModel).valid) {
      setSaveError('Проверьте расписание активации');
      return;
    }
    setSaving(true);
    setSaveError('');
    setConflict('');

    updateViolationRuleActivation(ruleId, {
      expected_schedule_version: rule.activation?.schedule_version ?? null,
      activation: toActivationDto(activationModel),
    })
      .then((updatedActivation) => {
        // Ответ PUT — сам activation, а не правило целиком: подменять им rule
        // нельзя, иначе карточка теряет все остальные поля правила.
        setRule((prev) => (prev ? { ...prev, activation: updatedActivation } : prev));
        setEditing(false);
        // FE-FIX07: колонка «Режим» в списке держит свой кэш расписаний —
        // после сохранения он обязан протухнуть.
        onActivationSaved?.();
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          // Форму НЕ перетираем и не закрываем: пользователь сам решает,
          // перечитать чужую версию или скопировать своё (ТЗ 11.2).
          setConflict(describeConflict(error, 'rule_schedule').message);
          return;
        }
        setSaveError(extractErrorMessage(error, 'Не удалось сохранить расписание'));
      })
      .finally(() => setSaving(false));
  };

  /**
   * Гейт ТЗ 1/2: редактирование core-полей разрешено только в INACTIVE, а
   * единственный способ туда попасть из ACTIVE — деактивировать. Оптимистично
   * открывать форму сразу после deactivate нельзя: backend может вернуть
   * outcome без реального изменения (например, уже отключено конкурентно),
   * поэтому решение принимает свежий GET, а не ответ POST /deactivate.
   */
  const startDeactivateAndEdit = () => setEditGate({ phase: 'confirm', message: '' });

  const runDeactivateAndEdit = () => {
    setEditGate({ phase: 'deactivating', message: '' });
    deactivateViolationRule(ruleId)
      .then(() => {
        setEditGate({ phase: 'refetching', message: '' });
        return fetchRule();
      })
      .then((fresh) => {
        setRule(fresh);
        if (deriveRuleState(fresh) !== RULE_STATE.INACTIVE) {
          setEditGate({
            phase: 'error',
            message: 'Backend не подтвердил деактивацию правила — редактирование недоступно. Обновите данные и повторите.',
          });
          return;
        }
        setEditGate(null);
        onEdit(fresh);
        // Строка в списке родителя обновлена не будет: деактивация ушла в
        // обход runLifecycle. Перечитываем список, чтобы он не показывал
        // устаревшее ACTIVE, пока пользователь редактирует/отменяет форму.
        onRefetch?.();
      })
      .catch((error) => {
        const conflictInfo = describeConflict(error, 'rule_core');
        setEditGate({
          phase: 'error',
          message: conflictInfo.kind === 'not_conflict'
            ? describeUserError(error, 'Не удалось деактивировать правило')
            : conflictInfo.message,
        });
      });
  };

  return (
    <>
      <div className="vio-drawer-overlay" onClick={onClose} />
      <aside className="vio-drawer" role="dialog" aria-label="Правило">
        <div className="vio-drawer-header">
          <h2 className="vio-drawer-title">Правило</h2>
          <button type="button" className="vio-drawer-close" onClick={onClose} aria-label="Закрыть">
            <Xmark width={20} height={20} />
          </button>
        </div>

        <div className="vio-drawer-body">
          {state === 'loading' && <ViolationStateBlock state="loading" />}
          {state === 'error' && <ViolationStateBlock state="error" message={errorMessage} />}

          {state === 'ready' && rule && (
            <>
              {canEdit && deriveRuleState(rule) === RULE_STATE.ACTIVE && (
                <p className="vio-field-hint">
                  Правило активно: поля доступны только для чтения. Чтобы изменить — сначала деактивируйте.
                </p>
              )}
              <div className="vio-readonly-grid">
                <div>
                  <div className="vio-readonly-label">Название</div>
                  <div className="vio-readonly-value">{rule.name || '—'}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Камера</div>
                  <div className="vio-readonly-value">{cameraLabel || `Камера #${rule.camera_id}`}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Контролируемый параметр</div>
                  <div className="vio-readonly-value">{metricLabel(rule.metric, catalog)}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Условие</div>
                  <div className="vio-readonly-value">{formatRuleCondition(rule, { catalog })}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Длительность</div>
                  <div className="vio-readonly-value">{rule.duration_seconds ?? '—'} сек</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Допустимый разрыв</div>
                  <div className="vio-readonly-value">{rule.allowed_gap_seconds ?? '—'} сек</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Состояние</div>
                  <div className="vio-readonly-value"><RuleStatusBadge rule={rule} /></div>
                </div>
                <div>
                  <div className="vio-readonly-label">Область контроля</div>
                  <div className="vio-readonly-value">
                    {/* FE-FIX29: zone_scope/zone_ids из ответа V2 авторитетнее
                        legacy zone_id, поэтому несколько зон показываются все. */}
                    {getZoneScopeLabel({
                      zoneIds: rule.zone_ids,
                      zoneId: rule.zone_id,
                      zoneScope: rule.zone_scope,
                      zones: catalogZones,
                      fallbackName: rule.zone_display_name,
                    })}
                  </div>
                </div>
                {/*
                  * FE-FIX17 / FE-FIX24: два независимых durable-переключателя.
                  * Показываем фактическое состояние backend, а не производную
                  * «эффективную» политику, которую фронт вычислил сам.
                  */}
                <div>
                  <div className="vio-readonly-label">Учёт в аналитике</div>
                  <div className="vio-readonly-value">
                    {rule.include_in_analytics === false
                      ? 'Не учитывается — события и кадры остаются в журнале'
                      : 'Учитывается'}
                  </div>
                </div>
                <div>
                  <div className="vio-readonly-label">Рассылка уведомлений</div>
                  <div className="vio-readonly-value">
                    {rule.notifications_enabled === false
                      ? 'Отключена для правила — письма и web push не отправляются'
                      : 'Включена — действуют настройки каналов получателей'}
                  </div>
                </div>
                {/* FE-FIX30: null-тройка — системная политика backend, не «пусто». */}
                <div>
                  <div className="vio-readonly-label">Повторяемость</div>
                  <div className="vio-readonly-value">{describeRecurrence(rule)}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Пауза</div>
                  <div className="vio-readonly-value">{formatCooldownShort(rule)}</div>
                </div>
                {isCooldownActive(rule.cooldown_until) && (
                  <div>
                    <div className="vio-readonly-label">Пауза действует до</div>
                    <div className="vio-readonly-value">{cooldownTzReady ? formatInTimezone(rule.cooldown_until, cooldownTimezone) : '…'}</div>
                  </div>
                )}
                {boostInfo.status === 'ready' && (
                  <>
                    <div>
                      <div className="vio-readonly-label">Ускорение детекций</div>
                      <div className="vio-readonly-value">{formatBoostShort(boostInfo.config)}</div>
                    </div>
                    {boostInfo.effective?.effective_interval_sec != null && (
                      <div>
                        <div className="vio-readonly-label">Интервал сейчас</div>
                        <div className="vio-readonly-value">{boostInfo.effective.effective_interval_sec} с</div>
                        {boostInfo.effective.boosted && (
                          <p className="vio-field-hint">Ускорение сейчас действует</p>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* Target additions Part 4, раздел 6.4: рисуем только то, что backend прислал. */}
                {Array.isArray(rule.required_features) && rule.required_features.length > 0 && (
                  <div>
                    <div className="vio-readonly-label">Используемая детекция</div>
                    <div className="vio-readonly-value">{featuresLabel(rule.required_features)}</div>
                  </div>
                )}
                {rule.origin && (
                  <div>
                    <div className="vio-readonly-label">Происхождение</div>
                    <div className="vio-readonly-value"><RuleOriginBadge origin={rule.origin} /></div>
                  </div>
                )}
                <div>
                  <div className="vio-readonly-label">Хранение событий</div>
                  <div className="vio-readonly-value">
                    {rule.event_retention_days == null ? 'По умолчанию' : `${rule.event_retention_days} дн.`}
                  </div>
                </div>
                <div>
                  <div className="vio-readonly-label">Хранение фото</div>
                  <div className="vio-readonly-value">
                    {rule.evidence_retention_days == null ? 'По умолчанию' : `${rule.evidence_retention_days} дн.`}
                  </div>
                </div>
                <div>
                  <div className="vio-readonly-label">Идентификатор</div>
                  <div className="vio-readonly-value">{rule.rule_id}</div>
                </div>
              </div>

              {boostInfo.status === 'error' && (
                <p className="vio-field-hint">Не удалось загрузить ускорение детекций</p>
              )}

              {retentionEditing ? (
                <>
                  <RetentionFields
                    value={retentionModel}
                    onChange={setRetentionModel}
                    errors={retentionErrors}
                    disabled={retentionSaving}
                    idPrefix="vio-rule-view"
                  />
                  {retentionConflict && (
                    <div className="vio-field-error">
                      {retentionConflict}{' '}
                      <button
                        type="button"
                        className="vio-btn vio-btn--inline-icon"
                        onClick={loadRule}
                        title="Обновить"
                        aria-label="Обновить"
                      >
                        <ArrowsRotateRight width={16} height={16} />
                      </button>
                    </div>
                  )}
                  {retentionError && <p className="vio-field-error">{retentionError}</p>}
                  <div className="vio-activation-actions">
                    <button
                      type="button"
                      className="vio-btn"
                      disabled={retentionSaving}
                      onClick={() => setRetentionEditing(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="vio-btn vio-btn--primary"
                      disabled={retentionSaving}
                      onClick={saveRetention}
                    >
                      {retentionSaving ? 'Сохраняем…' : 'Сохранить сроки хранения'}
                    </button>
                  </div>
                </>
              ) : canEdit && deriveRuleState(rule) !== RULE_STATE.DELETED && (
                <button type="button" className="vio-btn vio-btn--link" onClick={startRetentionEditing}>
                  Изменить сроки хранения
                </button>
              )}

              {editing ? (
                <>
                  <ActivationEditor
                    value={activationModel}
                    onChange={setActivationModel}
                    disabled={saving}
                  />
                  {conflict && (
                    <div className="vio-field-error">
                      {conflict}{' '}
                      <button
                        type="button"
                        className="vio-btn vio-btn--inline-icon"
                        onClick={loadRule}
                        title="Обновить"
                        aria-label="Обновить"
                      >
                        <ArrowsRotateRight width={16} height={16} />
                      </button>
                    </div>
                  )}
                  {saveError && <p className="vio-field-error">{saveError}</p>}
                  <div className="vio-activation-actions">
                    <button
                      type="button"
                      className="vio-btn"
                      disabled={saving}
                      onClick={() => setEditing(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="vio-btn vio-btn--primary"
                      disabled={saving}
                      onClick={saveActivation}
                    >
                      {saving ? 'Сохраняем…' : 'Сохранить расписание'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <ActivationSummary activation={rule.activation} />
                  {canEdit && rule.activation && (
                    <button type="button" className="vio-btn vio-btn--link" onClick={startEditing}>
                      Изменить расписание
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="vio-drawer-footer">
          {/* Правило можно выключить или удалить, не закрывая карточку. У удалённого
              правила lifecycle-кнопок нет: tombstone нельзя ни активировать, ни удалить
              повторно (Part 4, раздел 3.4). */}
          <div className="vio-drawer-actions">
            {rule && canEdit && deriveRuleState(rule) !== RULE_STATE.DELETED && (
              deriveRuleState(rule) === RULE_STATE.ACTIVE ? (
                <button type="button" className="vio-btn" disabled={busy} onClick={startDeactivateAndEdit}>
                  Деактивировать и изменить
                </button>
              ) : (
                <button type="button" className="vio-btn" disabled={busy} onClick={() => onEdit(rule)}>
                  Изменить
                </button>
              )
            )}
            {rule && deriveRuleState(rule) !== RULE_STATE.DELETED && (
              <>
                {deriveRuleState(rule) === RULE_STATE.ACTIVE && onDeactivate && (
                  <button type="button" className="vio-btn" disabled={busy} onClick={onDeactivate}>
                    {busy ? 'Отключаем…' : 'Деактивировать'}
                  </button>
                )}
                {deriveRuleState(rule) === RULE_STATE.INACTIVE && onActivate && (
                  <button type="button" className="vio-btn vio-btn--primary" disabled={busy} onClick={onActivate}>
                    {busy ? 'Активируем…' : 'Активировать'}
                  </button>
                )}
                {onDelete && (
                  <button type="button" className="vio-btn vio-btn--danger" disabled={busy} onClick={onDelete}>
                    Удалить
                  </button>
                )}
              </>
            )}
          </div>
          <button type="button" className="vio-btn" onClick={onClose}>Закрыть</button>
        </div>

        {editGate?.phase === 'error' && (
          <div className="vio-field-error">
            {editGate.message}{' '}
            <button type="button" className="vio-btn vio-btn--link" onClick={runDeactivateAndEdit}>Повторить</button>
            <button type="button" className="vio-btn vio-btn--link" onClick={() => setEditGate(null)}>Отмена</button>
          </div>
        )}
      </aside>

      {editGate && editGate.phase !== 'error' && (
        <>
          <div className="vio-modal-overlay" />
          <div className="vio-modal" role="dialog" aria-label="Деактивация правила">
            <h3 className="vio-modal-title">Деактивировать правило «{rule?.name}»?</h3>
            <p className="vio-modal-text">
              Правило перестанет срабатывать. После сохранения изменений его нужно будет активировать заново.
            </p>
            <div className="vio-modal-actions">
              <button
                type="button"
                className="vio-btn"
                disabled={editGate.phase !== 'confirm'}
                onClick={() => setEditGate(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="vio-btn vio-btn--primary"
                disabled={editGate.phase !== 'confirm'}
                onClick={runDeactivateAndEdit}
              >
                {editGate.phase === 'confirm' ? 'Деактивировать и открыть'
                  : editGate.phase === 'deactivating' ? 'Деактивируем…' : 'Перечитываем…'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ViolationRuleViewDrawer;
